import { FC, useCallback } from 'react';
import { useMutation } from 'react-relay';
import { useNavigate } from 'react-router';
import { Form, Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import SaveIcon from '@mui/icons-material/Save';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import TextField from 'components/TextField';

import { useErrorHandler } from 'lib';
import { useDialog } from 'components/Dialog';

import {
  CountriesListAddMutation,
  CountriesListAddMutation$data,
} from '__generated__/CountriesListAddMutation.graphql';

type FormFields = {
  name: string;
};

type Props = {
  connectionId: string;
};

const initial = {
  name: '',
};

const CountriesListAdd: FC<Props> = ({ connectionId }) => {
  const { close } = useDialog();
  const navigate = useNavigate();
  const errorHandler = useErrorHandler<CountriesListAddMutation$data>();

  const [add, loading] = useMutation<CountriesListAddMutation>(graphql`
    mutation CountriesListAddMutation($input: CreateCountryInput!) {
      createCountry(input: $input) {
        id
      }
    }
  `);

  const handleSubmit = useCallback(
    (input: FormFields) => {
      add({
        variables: { input },

        ...errorHandler({
          onCompleted: data => {
            navigate(data.createCountry.id);
          },
        }),

        updater: store => {
          store.get(connectionId)?.invalidateRecord();
        },
      });
    },
    [add, connectionId, errorHandler, navigate]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form>
        <DialogTitle>Nauja šalis</DialogTitle>

        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            Pridėkite naują šalį kuri vėliau gali būti naudojama kaip kelionės
            kryptis
          </DialogContentText>

          <TextField autoFocus name="name" label="Pavadinimas" fullWidth />
        </DialogContent>

        <DialogActions>
          <Button onClick={close} variant="text">
            Atšaukti
          </Button>

          <LoadingButton
            loading={loading}
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}>
            Saugoti
          </LoadingButton>
        </DialogActions>
      </Form>
    </Formik>
  );
};

export default CountriesListAdd;
